import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import { io } from "socket.io-client";
import { BASE_URL } from "../../constants";
import * as chatApi from "../../controllers/chatController";
import { playMessageBeep } from "../../utils/chatSound";
import { conversationTitle, messagePreview } from "./chatUtils";
import "./chatPopover.css";

// Real-time chat state for the logged-in user: one Socket.IO connection, the people directory with presence,
// conversations with unread counts, loaded message pages, typing indicators and the docked chat windows.

const ChatContext = createContext(null);
export const useChat = () => useContext(ChatContext);

const SETTINGS_KEY = "chatSettings";
const DEFAULT_SETTINGS = { sound: true, desktop: false };
const SEND_TIMEOUT_MS = 15000;
// Back in the foreground after this long hidden: reconnect even if the socket claims to be up.
const RESUME_RECONNECT_MS = 30 * 1000;
const TYPING_TTL_MS = 6000;

const readSettings = () => {
  try {
    return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || "{}") };
  } catch {
    return DEFAULT_SETTINGS;
  }
};

// Open chats are shown as round "chat heads" at the bottom of the screen; one of them can be expanded.
const MAX_CHAT_HEADS = 6;

const initialState = {
  status: "connecting",
  me: null,
  users: {},
  conversations: {},
  messages: {}, // conversationId -> { items, hasMore, loaded, loading }
  typing: {}, // conversationId -> { userId: true }
  windows: [], // chat heads, oldest first: [{ id, minimized }]; at most one is expanded (minimized: false)
  focus: null, // { conversationId, messageId, nonce } – a message to scroll to and highlight
  pinsVersion: {}, // conversationId -> counter, bumped when a pin changes (views refetch their pins)
  pageMode: false, // the full chat page is open: conversations open there instead of in docked windows
  pageConversationId: null,
  settings: readSettings(),
};

const byId = (list) => Object.fromEntries(list.map((item) => [item.id, item]));

// Inserts or replaces a confirmed message, keeping confirmed messages ordered by id and pending ones last.
const upsertMessage = (items, message) => {
  const index = items.findIndex((m) => !m.pending && m.id === message.id);
  if (index >= 0) {
    const next = items.slice();
    next[index] = message;
    return next;
  }
  const confirmed = items.filter((m) => !m.pending);
  const pending = items.filter((m) => m.pending);
  let at = confirmed.length;
  while (at > 0 && confirmed[at - 1].id > message.id) at -= 1;
  return [...confirmed.slice(0, at), message, ...confirmed.slice(at), ...pending];
};

const withMessages = (state, conversationId, update) => {
  const current = state.messages[conversationId] || { items: [], hasMore: true, loaded: false, loading: false };
  return { ...state.messages, [conversationId]: { ...current, ...update(current) } };
};

const withConversation = (state, conversationId, update) => {
  const conversation = state.conversations[conversationId];
  if (!conversation) return state.conversations;
  return { ...state.conversations, [conversationId]: { ...conversation, ...update(conversation) } };
};

// Keeps at most MAX_CHAT_HEADS, dropping the oldest collapsed ones first.
function trimHeads(windows) {
  const result = [...windows];
  while (result.length > MAX_CHAT_HEADS) {
    const index = result.findIndex((w) => w.minimized);
    result.splice(index === -1 ? 0 : index, 1);
  }
  return result;
}

function reducer(state, action) {
  switch (action.type) {
    case "STATUS":
      return { ...state, status: action.status };
    case "BOOTSTRAP":
      return {
        ...state,
        me: action.me,
        users: byId(action.users),
        conversations: byId(action.conversations),
      };
    case "CONVERSATIONS": {
      // Fresh list from the server: drop chats (and their heads/messages) that no longer exist.
      const conversations = byId(action.conversations);
      const messages = Object.fromEntries(Object.entries(state.messages).filter(([id]) => conversations[id]));
      return {
        ...state,
        conversations,
        messages,
        windows: state.windows.filter((w) => conversations[w.id]),
        pageConversationId: conversations[state.pageConversationId] ? state.pageConversationId : null,
      };
    }
    case "USERS":
      return { ...state, users: byId(action.users) };
    case "USER_UPDATED": {
      // Name, title or photo changed (profile edit, HR link). Keep the presence we already track.
      const existing = state.users[action.user.id];
      const user = { ...existing, ...action.user, online: existing ? existing.online : action.user.online };
      return {
        ...state,
        users: { ...state.users, [user.id]: user },
        me: state.me?.id === user.id ? { ...state.me, ...user } : state.me,
      };
    }
    case "PRESENCE": {
      const user = state.users[action.userId];
      if (!user) return state;
      return {
        ...state,
        users: {
          ...state.users,
          [action.userId]: { ...user, online: action.online, lastSeenAt: action.lastSeenAt || user.lastSeenAt },
        },
      };
    }
    case "CONVERSATION_UPSERT": {
      const existing = state.conversations[action.conversation.id];
      return {
        ...state,
        conversations: {
          ...state.conversations,
          [action.conversation.id]: {
            unreadCount: 0,
            mentionCount: 0,
            muted: false,
            lastMessage: null,
            ...existing,
            ...action.conversation,
          },
        },
      };
    }
    case "CONVERSATION_REMOVE": {
      const conversations = { ...state.conversations };
      const messages = { ...state.messages };
      delete conversations[action.id];
      delete messages[action.id];
      return { ...state, conversations, messages, windows: state.windows.filter((w) => w.id !== action.id) };
    }
    case "MESSAGES_LOADING":
      return { ...state, messages: withMessages(state, action.conversationId, () => ({ loading: true })) };
    case "MESSAGES_LOADED":
      return {
        ...state,
        messages: withMessages(state, action.conversationId, (current) => {
          const pending = current.items.filter((m) => m.pending);
          const base = action.replace ? pending : current.items;
          return {
            items: action.messages.reduce(upsertMessage, base),
            hasMore: action.hasMore,
            loaded: true,
            loading: false,
          };
        }),
      };
    case "MESSAGES_FAILED":
      return { ...state, messages: withMessages(state, action.conversationId, () => ({ loading: false })) };
    case "MESSAGE_RECEIVED": {
      const { message } = action;
      const conversationId = message.conversationId;
      const loaded = state.messages[conversationId]?.loaded;
      return {
        ...state,
        messages: loaded
          ? withMessages(state, conversationId, (current) => ({
              items: upsertMessage(
                message.clientId ? current.items.filter((m) => m.clientId !== message.clientId) : current.items,
                message
              ),
            }))
          : state.messages,
        conversations: withConversation(state, conversationId, (conversation) => {
          const isNewer = !conversation.lastMessage || message.id >= conversation.lastMessage.id;
          return {
            lastMessage: isNewer ? message : conversation.lastMessage,
            lastMessageAt: isNewer ? message.createdAt : conversation.lastMessageAt,
            unreadCount: conversation.unreadCount + (action.countUnread ? 1 : 0),
            mentionCount: (conversation.mentionCount || 0) + (action.countUnread && action.mentionsMe ? 1 : 0),
            // The sender has read up to their own message.
            members: conversation.members.map((m) =>
              m.userId === message.senderId ? { ...m, lastReadMessageId: message.id } : m
            ),
          };
        }),
      };
    }
    case "MESSAGE_UPDATED": {
      const { message } = action;
      const conversationId = message.conversationId;
      const previous = state.messages[conversationId]?.items.find((m) => m.id === message.id);
      const pinChanged = !previous || Boolean(previous.pinnedAt) !== Boolean(message.pinnedAt);
      return {
        ...state,
        pinsVersion: pinChanged
          ? { ...state.pinsVersion, [conversationId]: (state.pinsVersion[conversationId] || 0) + 1 }
          : state.pinsVersion,
        messages: state.messages[conversationId]?.loaded
          ? withMessages(state, conversationId, (current) => ({
              items: current.items.some((m) => m.id === message.id)
                ? upsertMessage(current.items, message)
                : current.items,
            }))
          : state.messages,
        conversations: withConversation(state, conversationId, (conversation) =>
          conversation.lastMessage?.id === message.id ? { lastMessage: message } : {}
        ),
      };
    }
    case "PENDING_ADD":
      return {
        ...state,
        messages: withMessages(state, action.message.conversationId, (current) => ({
          items: [...current.items.filter((m) => m.clientId !== action.message.clientId), action.message],
        })),
      };
    case "PENDING_PATCH":
      return {
        ...state,
        messages: withMessages(state, action.conversationId, (current) => ({
          items: current.items.map((m) => (m.clientId === action.clientId ? { ...m, ...action.patch } : m)),
        })),
      };
    case "PENDING_RESOLVE":
      return {
        ...state,
        messages: withMessages(state, action.conversationId, (current) => {
          if (action.error) {
            return {
              items: current.items.map((m) =>
                m.clientId === action.clientId ? { ...m, failed: true, error: action.error } : m
              ),
            };
          }
          const items = current.items.filter((m) => m.clientId !== action.clientId);
          return { items: action.message ? upsertMessage(items, action.message) : items };
        }),
      };
    case "READ_UPDATE":
      return {
        ...state,
        conversations: withConversation(state, action.conversationId, (conversation) => ({
          unreadCount: action.userId === state.me?.id ? 0 : conversation.unreadCount,
          mentionCount: action.userId === state.me?.id ? 0 : conversation.mentionCount,
          members: conversation.members.map((m) =>
            m.userId === action.userId && (m.lastReadMessageId || 0) < action.messageId
              ? { ...m, lastReadMessageId: action.messageId }
              : m
          ),
        })),
      };
    case "TYPING": {
      const current = { ...(state.typing[action.conversationId] || {}) };
      if (action.isTyping) current[action.userId] = true;
      else delete current[action.userId];
      return { ...state, typing: { ...state.typing, [action.conversationId]: current } };
    }
    case "MUTED":
      return {
        ...state,
        conversations: withConversation(state, action.conversationId, () => ({ muted: action.muted })),
      };
    case "CONVERSATION_PINNED":
      return {
        ...state,
        conversations: withConversation(state, action.conversationId, () => ({ pinnedAt: action.pinnedAt })),
      };
    case "OPEN_WINDOW": {
      // Expands the conversation's chat head (adding one if needed) and collapses the others.
      if (state.pageMode) return { ...state, pageConversationId: action.id };
      const exists = state.windows.some((w) => w.id === action.id);
      const windows = (exists ? state.windows : [...state.windows, { id: action.id }]).map((w) => ({
        id: w.id,
        minimized: w.id !== action.id,
      }));
      return { ...state, windows: trimHeads(windows) };
    }
    case "ADD_HEAD": {
      // A collapsed chat head for an incoming message; leaves the expanded chat alone.
      if (state.pageMode || state.windows.some((w) => w.id === action.id)) return state;
      return { ...state, windows: trimHeads([...state.windows, { id: action.id, minimized: true }]) };
    }
    case "CLOSE_WINDOW":
      return { ...state, windows: state.windows.filter((w) => w.id !== action.id) };
    case "TOGGLE_MINIMIZE": {
      const target = state.windows.find((w) => w.id === action.id);
      if (!target) return state;
      const expanding = target.minimized;
      return {
        ...state,
        windows: state.windows.map((w) => ({ id: w.id, minimized: w.id === action.id ? !expanding : true })),
      };
    }
    case "PAGE_MODE":
      return {
        ...state,
        pageMode: action.on,
        pageConversationId: action.on ? action.conversationId ?? state.pageConversationId : null,
        // Opening the page takes over the docked chats.
        windows: action.on ? [] : state.windows,
      };
    case "FOCUS_MESSAGE":
      return { ...state, focus: action.focus };
    case "SETTINGS":
      return { ...state, settings: { ...state.settings, ...action.settings } };
    default:
      return state;
  }
}

export function ChatProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const stateRef = useRef(state);
  stateRef.current = state;
  const socketRef = useRef(null);
  const visibleRef = useRef(new Map()); // conversationId -> number of mounted, visible views
  const typingTimers = useRef({});
  const olderInFlight = useRef(new Set());
  const openConversationRef = useRef(null);
  // Messages written while the connection was down; sent in order once it's back (flushQueueRef).
  const sendQueueRef = useRef([]);
  const flushQueueRef = useRef(() => {});
  const socketListeners = useRef(new Map()); // event -> Set of handlers registered through subscribe()

  const isConversationVisible = (conversationId) =>
    (visibleRef.current.get(conversationId) || 0) > 0 && document.visibilityState === "visible";

  const refreshUsers = useCallback(async () => {
    dispatch({ type: "USERS", users: await chatApi.fetchChatUsers() });
  }, []);

  // The server says a chat we're showing doesn't exist (deleted, or you were removed): drop it and resync the list.
  const forgetConversation = useCallback(async (conversationId) => {
    dispatch({ type: "CONVERSATION_REMOVE", id: conversationId });
    try {
      dispatch({ type: "CONVERSATIONS", conversations: await chatApi.fetchConversations() });
    } catch {
      /* the list resyncs on the next reconnect */
    }
  }, []);

  const loadLatest = useCallback(async (conversationId) => {
    dispatch({ type: "MESSAGES_LOADING", conversationId });
    try {
      const { messages, hasMore } = await chatApi.fetchMessages(conversationId);
      dispatch({ type: "MESSAGES_LOADED", conversationId, messages, hasMore, replace: true });
    } catch (err) {
      dispatch({ type: "MESSAGES_FAILED", conversationId });
      if (err.response?.status === 404) forgetConversation(conversationId);
    }
  }, [forgetConversation]);

  const markRead = useCallback((conversationId, messageId) => {
    const { me, conversations } = stateRef.current;
    const conversation = conversations[conversationId];
    if (!me || !conversation || !messageId) return;
    const mine = conversation.members.find((m) => m.userId === me.id);
    if (conversation.unreadCount === 0 && mine && (mine.lastReadMessageId || 0) >= messageId) return;
    dispatch({ type: "READ_UPDATE", conversationId, userId: me.id, messageId });
    socketRef.current?.emit("conversation:read", { conversationId, messageId });
  }, []);

  const notifyDesktop = (message) => {
    const { users, conversations, me } = stateRef.current;
    const conversation = conversations[message.conversationId];
    if (!conversation || !("Notification" in window) || Notification.permission !== "granted") return;
    const sender = users[message.senderId];
    const mentioned = (message.mentionedUserIds || []).includes(me?.id);
    const title = mentioned
      ? `${sender?.name || "Someone"} mentioned you${conversation.type === "group" ? ` in ${conversation.name}` : ""}`
      : conversation.type === "group"
        ? `${sender?.name || "Someone"} in ${conversation.name}`
        : sender?.name || "New message";
    const notification = new Notification(title, {
      body: messagePreview(message, users, me?.id),
      icon: "/logo192.png",
      tag: `chat-${message.conversationId}`,
    });
    notification.onclick = () => {
      window.focus();
      openConversationRef.current?.(message.conversationId);
      notification.close();
    };
  };

  // ---------- Socket lifecycle ----------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return undefined;

    const socket = io(BASE_URL, {
      path: "/api/socket.io",
      auth: { token },
      transports: ["websocket", "polling"],
    });
    socketRef.current = socket;

    const bootstrap = async () => {
      try {
        const [me, users, conversations] = await Promise.all([
          chatApi.fetchChatMe(),
          chatApi.fetchChatUsers(),
          chatApi.fetchConversations(),
        ]);
        dispatch({ type: "BOOTSTRAP", me, users, conversations });
        // After a reconnect, re-sync any conversation whose messages were already loaded.
        Object.entries(stateRef.current.messages)
          .filter(([, page]) => page.loaded)
          .forEach(([conversationId]) => loadLatest(Number(conversationId)));
      } catch (err) {
        console.error("Failed to load chat:", err);
      }
    };

    socket.on("connect", () => {
      dispatch({ type: "STATUS", status: "connected" });
      bootstrap();
      flushQueueRef.current();
      socketListeners.current.get("connect")?.forEach((handler) => handler());
    });
    // Other features (e.g. the notice board) share this connection through subscribe().
    socket.onAny((event, payload) => socketListeners.current.get(event)?.forEach((handler) => handler(payload)));
    socket.on("disconnect", () => dispatch({ type: "STATUS", status: "disconnected" }));
    let unauthorized = false;
    socket.on("connect_error", (err) => {
      dispatch({ type: "STATUS", status: "disconnected" });
      if (err.message === "unauthorized") {
        unauthorized = true;
        socket.disconnect();
      }
    });

    // Phones freeze background apps: on return the socket may be waiting out a reconnect backoff, or
    // look connected while it's dead. Coming back (or regaining network) reconnects at once; after a
    // longer sleep it reconnects anyway, and the connect handler re-syncs whatever was missed.
    let hiddenAt = document.visibilityState === "hidden" ? Date.now() : null;
    const resume = () => {
      if (unauthorized || document.visibilityState === "hidden") return;
      const sleptLong = hiddenAt !== null && Date.now() - hiddenAt > RESUME_RECONNECT_MS;
      hiddenAt = null;
      if (!socket.connected) socket.connect();
      else if (sleptLong) socket.disconnect().connect();
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") hiddenAt = Date.now();
      else resume();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", resume);
    window.addEventListener("pageshow", resume);

    socket.on("presence:update", (presence) => dispatch({ type: "PRESENCE", ...presence }));
    socket.on("user:updated", (user) => dispatch({ type: "USER_UPDATED", user }));

    socket.on("conversation:upsert", (conversation) => {
      dispatch({ type: "CONVERSATION_UPSERT", conversation });
      const { users } = stateRef.current;
      if (conversation.members.some((m) => !users[m.userId])) refreshUsers();
    });
    socket.on("conversation:removed", ({ conversationId }) =>
      dispatch({ type: "CONVERSATION_REMOVE", id: conversationId })
    );
    // Pinning is per user; this keeps the user's other tabs and devices in step.
    socket.on("conversation:pinned", ({ conversationId, pinnedAt }) =>
      dispatch({ type: "CONVERSATION_PINNED", conversationId, pinnedAt })
    );

    socket.on("message:new", async (message) => {
      const { me, conversations, settings } = stateRef.current;
      const fromOther = Boolean(message.senderId) && message.senderId !== me?.id;
      const visible = isConversationVisible(message.conversationId);

      if (!conversations[message.conversationId]) {
        try {
          const conversation = await chatApi.fetchConversation(message.conversationId);
          dispatch({ type: "CONVERSATION_UPSERT", conversation });
        } catch {
          return;
        }
      }
      const mentionsMe = (message.mentionedUserIds || []).includes(me?.id);
      dispatch({
        type: "MESSAGE_RECEIVED",
        message,
        countUnread: fromOther && !visible && message.type !== "system",
        mentionsMe,
      });
      dispatch({ type: "TYPING", conversationId: message.conversationId, userId: message.senderId, isTyping: false });

      if (!fromOther) return;
      if (visible) markRead(message.conversationId, message.id);
      const conversation = stateRef.current.conversations[message.conversationId];
      // Mentions get through a muted chat.
      if (message.type === "system" || (conversation?.muted && !mentionsMe)) return;
      // Direct messages and mentions pop up as a chat head (collapsed) so they're one click away.
      if (!visible && (conversation?.type === "direct" || mentionsMe)) {
        dispatch({ type: "ADD_HEAD", id: message.conversationId });
      }
      const pageInBackground = document.visibilityState !== "visible" || !document.hasFocus();
      if (settings.sound && (!visible || pageInBackground)) playMessageBeep();
      if (settings.desktop && pageInBackground) notifyDesktop(message);
    });

    socket.on("message:updated", (message) => dispatch({ type: "MESSAGE_UPDATED", message }));
    socket.on("read:update", (read) => dispatch({ type: "READ_UPDATE", ...read }));

    socket.on("typing", ({ conversationId, userId, isTyping }) => {
      const key = `${conversationId}:${userId}`;
      clearTimeout(typingTimers.current[key]);
      dispatch({ type: "TYPING", conversationId, userId, isTyping });
      if (isTyping) {
        typingTimers.current[key] = setTimeout(
          () => dispatch({ type: "TYPING", conversationId, userId, isTyping: false }),
          TYPING_TTL_MS
        );
      }
    });

    const timers = typingTimers.current;
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", resume);
      window.removeEventListener("pageshow", resume);
      Object.values(timers).forEach(clearTimeout);
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Unread badge: tab title and installed-app icon ----------
  const totalUnread = useMemo(
    () =>
      Object.values(state.conversations).reduce(
        // Muted chats only count messages that mention you.
        (sum, c) => sum + (c.muted ? c.mentionCount || 0 : c.unreadCount || 0),
        0
      ),
    [state.conversations]
  );

  // The header rewrites document.title on every page change, so re-apply the "(n) " prefix whenever
  // the <title> changes rather than only when the count does.
  useEffect(() => {
    const applyPrefix = () => {
      const base = document.title.replace(/^\(\d+\)\s/, "");
      const next = totalUnread ? `(${totalUnread}) ${base}` : base;
      if (document.title !== next) document.title = next;
    };
    applyPrefix();
    const titleEl = document.querySelector("title");
    const observer = titleEl ? new MutationObserver(applyPrefix) : null;
    observer?.observe(titleEl, { childList: true, characterData: true, subtree: true });
    if ("setAppBadge" in navigator) {
      (totalUnread ? navigator.setAppBadge(totalUnread) : navigator.clearAppBadge()).catch(() => {});
    }
    return () => observer?.disconnect();
  }, [totalUnread]);

  useEffect(
    () => () => {
      document.title = document.title.replace(/^\(\d+\)\s/, "");
      if ("clearAppBadge" in navigator) navigator.clearAppBadge().catch(() => {});
    },
    []
  );

  // Listens to a server event on the shared socket ("connect" fires on every (re)connect). Returns an unsubscribe.
  const subscribe = useCallback((event, handler) => {
    const listeners = socketListeners.current;
    if (!listeners.has(event)) listeners.set(event, new Set());
    listeners.get(event).add(handler);
    return () => listeners.get(event)?.delete(handler);
  }, []);

  // ---------- Actions ----------
  const openConversation = useCallback((id) => {
    dispatch({ type: "OPEN_WINDOW", id });
  }, []);
  openConversationRef.current = openConversation;

  // A push notification was clicked while the app was already open (sent by the service worker).
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;
    const onMessage = (event) => {
      if (event.data?.type === "open-chat" && event.data.conversationId) {
        openConversationRef.current?.(Number(event.data.conversationId));
      }
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, []);

  const setPageMode = useCallback(
    (on, conversationId) => dispatch({ type: "PAGE_MODE", on, conversationId }),
    []
  );

  const closeWindow = useCallback((id) => dispatch({ type: "CLOSE_WINDOW", id }), []);
  const toggleMinimize = useCallback((id) => dispatch({ type: "TOGGLE_MINIMIZE", id }), []);

  const ensureMessages = useCallback(
    (conversationId) => {
      const page = stateRef.current.messages[conversationId];
      if (!page || (!page.loaded && !page.loading)) loadLatest(conversationId);
    },
    [loadLatest]
  );

  // Loads the page before `before` (default: the oldest loaded message). Resolves to the fetched
  // { messages, hasMore }, or null when nothing was requested.
  const loadOlder = useCallback(async (conversationId, before) => {
    const page = stateRef.current.messages[conversationId];
    if (olderInFlight.current.has(conversationId)) return null;
    let cursor = before;
    if (cursor === undefined) {
      if (!page || !page.hasMore) return null;
      cursor = page.items.find((m) => !m.pending)?.id;
    }
    olderInFlight.current.add(conversationId);
    dispatch({ type: "MESSAGES_LOADING", conversationId });
    try {
      const result = await chatApi.fetchMessages(conversationId, { before: cursor });
      dispatch({ type: "MESSAGES_LOADED", conversationId, messages: result.messages, hasMore: result.hasMore });
      return result;
    } catch {
      dispatch({ type: "MESSAGES_FAILED", conversationId });
      return null;
    } finally {
      olderInFlight.current.delete(conversationId);
    }
  }, []);

  const getLoadedMessages = useCallback((conversationId) => stateRef.current.messages[conversationId]?.items || [], []);

  const emitSend = useCallback((pending) => {
    const socket = socketRef.current;
    if (!socket) return;
    // Offline: hold the message (shown as "Waiting for network") and send it on reconnect. Only
    // messages that never left are queued, so a resend can't duplicate one the server already has.
    if (!socket.connected) {
      sendQueueRef.current = [...sendQueueRef.current.filter((m) => m.clientId !== pending.clientId), pending];
      dispatch({ type: "PENDING_PATCH", conversationId: pending.conversationId, clientId: pending.clientId, patch: { queued: true } });
      return;
    }
    socket.timeout(SEND_TIMEOUT_MS).emit(
      "message:send",
      {
        conversationId: pending.conversationId,
        body: pending.body,
        replyToId: pending.replyTo?.id,
        clientId: pending.clientId,
        attachmentIds: pending.attachments.map((a) => a.id),
      },
      (err, response) => {
        const error = err ? "Not sent – connection problem" : !response?.ok ? response?.error || "Not sent" : null;
        dispatch({
          type: "PENDING_RESOLVE",
          conversationId: pending.conversationId,
          clientId: pending.clientId,
          message: error ? null : response.message,
          error,
        });
        if (!error) dispatch({ type: "MESSAGE_RECEIVED", message: response.message, countUnread: false });
        if (response?.error === "Conversation not found") forgetConversation(pending.conversationId);
      }
    );
  }, [forgetConversation]);

  flushQueueRef.current = () => {
    const queue = sendQueueRef.current;
    sendQueueRef.current = [];
    queue.forEach((pending) => {
      dispatch({ type: "PENDING_PATCH", conversationId: pending.conversationId, clientId: pending.clientId, patch: { queued: false } });
      emitSend({ ...pending, queued: false });
    });
  };

  const discardQueued = (clientId) => {
    sendQueueRef.current = sendQueueRef.current.filter((m) => m.clientId !== clientId);
  };

  const sendMessage = useCallback(
    (conversationId, { body, replyTo, attachments = [] }) => {
      const { me } = stateRef.current;
      const pending = {
        clientId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        pending: true,
        conversationId,
        senderId: me?.id,
        type: "text",
        body: body || null,
        replyTo: replyTo
          ? { id: replyTo.id, senderId: replyTo.senderId, body: replyTo.body, attachmentCount: replyTo.attachments?.length || 0 }
          : null,
        attachments,
        reactions: [],
        createdAt: new Date().toISOString(),
      };
      dispatch({ type: "PENDING_ADD", message: pending });
      emitSend(pending);
    },
    [emitSend]
  );

  const retryMessage = useCallback(
    (conversationId, clientId) => {
      const pending = stateRef.current.messages[conversationId]?.items.find((m) => m.clientId === clientId);
      if (!pending) return;
      const retry = { ...pending, failed: false, error: null };
      dispatch({ type: "PENDING_ADD", message: retry });
      emitSend(retry);
    },
    [emitSend]
  );

  const discardMessage = useCallback((conversationId, clientId) => {
    discardQueued(clientId);
    dispatch({ type: "PENDING_RESOLVE", conversationId, clientId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editMessage = useCallback(async (messageId, body) => {
    dispatch({ type: "MESSAGE_UPDATED", message: await chatApi.editChatMessage(messageId, body) });
  }, []);

  const deleteMessage = useCallback(async (messageId) => {
    dispatch({ type: "MESSAGE_UPDATED", message: await chatApi.deleteChatMessage(messageId) });
  }, []);

  const toggleReaction = useCallback(async (messageId, emoji) => {
    dispatch({ type: "MESSAGE_UPDATED", message: await chatApi.toggleChatReaction(messageId, emoji) });
  }, []);

  const setPinned = useCallback(async (messageId, pinned) => {
    const message = await (pinned ? chatApi.pinChatMessage(messageId) : chatApi.unpinChatMessage(messageId));
    dispatch({ type: "MESSAGE_UPDATED", message });
  }, []);

  const sendTyping = useCallback((conversationId, isTyping) => {
    socketRef.current?.emit("typing", { conversationId, isTyping });
  }, []);

  const startDirectChat = useCallback(
    async (userId) => {
      const conversation = await chatApi.createDirectConversation(userId);
      dispatch({ type: "CONVERSATION_UPSERT", conversation });
      openConversation(conversation.id);
      return conversation;
    },
    [openConversation]
  );

  const createGroup = useCallback(
    async (group) => {
      const conversation = await chatApi.createGroupConversation(group);
      dispatch({ type: "CONVERSATION_UPSERT", conversation });
      openConversation(conversation.id);
      return conversation;
    },
    [openConversation]
  );

  const applyConversation = (conversation) => dispatch({ type: "CONVERSATION_UPSERT", conversation });

  const updateGroup = useCallback(async (id, changes) => {
    applyConversation(await chatApi.updateGroupConversation(id, changes));
  }, []);
  const addMembers = useCallback(async (id, userIds) => {
    applyConversation(await chatApi.addConversationMembers(id, userIds));
  }, []);
  const removeMember = useCallback(async (id, userId) => {
    await chatApi.removeConversationMember(id, userId);
    if (userId === stateRef.current.me?.id) dispatch({ type: "CONVERSATION_REMOVE", id });
  }, []);
  const setMemberRole = useCallback(async (id, userId, role) => {
    applyConversation(await chatApi.setConversationMemberRole(id, userId, role));
  }, []);
  const setMuted = useCallback(async (id, muted) => {
    const result = await chatApi.setConversationMuted(id, muted);
    dispatch({ type: "MUTED", conversationId: id, muted: result.muted });
  }, []);
  const setConversationPinned = useCallback(async (id, pinned) => {
    const result = await chatApi.setConversationPinned(id, pinned);
    dispatch({ type: "CONVERSATION_PINNED", conversationId: id, pinnedAt: result.pinnedAt });
  }, []);
  // "Delete for me": the chat leaves your list until someone writes again.
  const hideConversation = useCallback(async (id) => {
    await chatApi.hideConversation(id);
    dispatch({ type: "CONVERSATION_REMOVE", id });
  }, []);
  const deleteGroup = useCallback(async (id) => {
    await chatApi.deleteGroupConversation(id);
    dispatch({ type: "CONVERSATION_REMOVE", id });
  }, []);
  const setGroupPhoto = useCallback(async (id, blob, onProgress) => {
    applyConversation(await chatApi.uploadGroupPhoto(id, blob, onProgress));
  }, []);
  const removeGroupPhoto = useCallback(async (id) => {
    applyConversation(await chatApi.removeGroupPhoto(id));
  }, []);

  const focusMessage = useCallback(
    (conversationId, messageId) => {
      openConversation(conversationId);
      dispatch({ type: "FOCUS_MESSAGE", focus: { conversationId, messageId, nonce: Date.now() } });
    },
    [openConversation]
  );
  const clearFocus = useCallback(() => dispatch({ type: "FOCUS_MESSAGE", focus: null }), []);

  const updateSettings = useCallback(async (changes) => {
    const next = { ...changes };
    if (next.desktop && "Notification" in window && Notification.permission !== "granted") {
      next.desktop = (await Notification.requestPermission()) === "granted";
    }
    dispatch({ type: "SETTINGS", settings: next });
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify({ ...stateRef.current.settings, ...next }));
    } catch {
      /* settings just won't persist */
    }
  }, []);

  // Views call this while a conversation is on screen so incoming messages are marked read, not beeped.
  const registerVisible = useCallback((conversationId) => {
    const map = visibleRef.current;
    map.set(conversationId, (map.get(conversationId) || 0) + 1);
    return () => {
      const remaining = (map.get(conversationId) || 1) - 1;
      if (remaining > 0) map.set(conversationId, remaining);
      else map.delete(conversationId);
    };
  }, []);

  // Pinned chats first (most recently pinned on top), then by latest activity.
  const sortedConversations = useMemo(
    () =>
      Object.values(state.conversations).sort(
        (a, b) =>
          new Date(b.pinnedAt || 0) - new Date(a.pinnedAt || 0) ||
          new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt)
      ),
    [state.conversations]
  );

  const titleOf = useCallback(
    (conversation) => conversationTitle(conversation, state.users, state.me?.id),
    [state.users, state.me]
  );

  const value = {
    ...state,
    totalUnread,
    sortedConversations,
    titleOf,
    openConversation,
    setPageMode,
    closeWindow,
    toggleMinimize,
    ensureMessages,
    loadOlder,
    getLoadedMessages,
    sendMessage,
    retryMessage,
    discardMessage,
    editMessage,
    deleteMessage,
    toggleReaction,
    setPinned,
    sendTyping,
    markRead,
    startDirectChat,
    createGroup,
    updateGroup,
    addMembers,
    removeMember,
    setMemberRole,
    setMuted,
    setConversationPinned,
    hideConversation,
    deleteGroup,
    setGroupPhoto,
    removeGroupPhoto,
    focusMessage,
    clearFocus,
    updateSettings,
    registerVisible,
    subscribe,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
